import { resolveEnableFlag } from '@/features/order/order-form/lib/enable-flag'
import type { OrderFormCollapsibleConfig } from '@/state/order'

/**
 * True when the Exit Condition editor holds any usable bracket data — i.e. a
 * take-profit and/or stop-loss price. Mirrors hasTriggerData in trigger-active.ts.
 */
export function hasExitData(config: OrderFormCollapsibleConfig | null | undefined): boolean {
  if (!config) return false
  return Boolean(config.priceAbove || config.priceBelow)
}

/**
 * Whether the exit bracket should attach its TP/SL legs on submit.
 * - `'true'`  → on
 * - `'false'` → off (prices kept but no legs attached)
 * - unset     → on when a price is present (matches the auto-on-when-edited
 *   model and keeps brackets saved before the flag existed). Same shape as
 *   isTriggerActive.
 */
export function isExitActive(config: OrderFormCollapsibleConfig | null | undefined): boolean {
  if (!config) return false
  return resolveEnableFlag(config.exitConditionEnabled, hasExitData(config))
}

/**
 * The order form no longer has an Exit Condition section. A TP/SL saved before it was
 * removed (or written into the config some other way) has no UI left to see or clear
 * it, so the form reads its config through this: the bracket is always off and nothing
 * hidden can ride along on a submit. Everything else in the config is kept.
 */
export function withoutExitBracket(
  config: OrderFormCollapsibleConfig | null | undefined
): OrderFormCollapsibleConfig {
  return { ...config, exitConditionEnabled: 'false' }
}
